import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { CalendarClock, Archive, Save, CheckCircle2, Lock } from "lucide-react";
import { toast } from "sonner";
import { formatWIB } from "@/lib/datetime";
import { AKHIR_BULAN, TANGGAL_MULAI_MAKS, labelTanggalSelesai, opsiTanggalSelesai, teksRentangPeriode } from "@/lib/periodeEdit";

const DAYS_MULAI = Array.from({ length: TANGGAL_MULAI_MAKS }, (_, i) => i + 1);

export default function PengaturanPeriode() {
    const [cfg, setCfg] = useState(null);
    const [mulai, setMulai] = useState("1");
    const [selesai, setSelesai] = useState("10");
    const [saving, setSaving] = useState(false);

    const [snapshots, setSnapshots] = useState([]);
    const [creating, setCreating] = useState(false);
    const [confirmOverwrite, setConfirmOverwrite] = useState(null);

    const loadCfg = async () => {
        try {
            const { data } = await api.get("/periode-config");
            setCfg(data);
            setMulai(String(data.tanggal_mulai));
            setSelesai(String(data.tanggal_selesai));
        } catch { toast.error("Gagal memuat konfigurasi periode"); }
    };
    const loadSnapshots = async () => {
        try { const { data } = await api.get("/snapshot/list"); setSnapshots(data); } catch {}
    };

    useEffect(() => { loadCfg(); loadSnapshots(); }, []);

    const save = async () => {
        if (Number(mulai) > Number(selesai)) return toast.error("Tanggal mulai tidak boleh setelah tanggal selesai");
        setSaving(true);
        try {
            await api.put("/periode-config", { tanggal_mulai: Number(mulai), tanggal_selesai: Number(selesai) });
            toast.success("Periode edit berhasil disimpan");
            loadCfg();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail));
        } finally {
            setSaving(false);
        }
    };

    const createSnapshot = async (overwrite = false) => {
        setCreating(true);
        try {
            const { data } = await api.post("/snapshot/create", { overwrite });
            toast.success(`Snapshot ${data.snapshot?.label || "bulan ini"} berhasil dibuat`);
            loadSnapshots();
        } catch (e) {
            const detail = e.response?.data?.detail || "";
            if (e.response?.status === 409) {
                setConfirmOverwrite(detail);
            } else {
                toast.error(formatApiErrorDetail(detail));
            }
        } finally {
            setCreating(false);
        }
    };

    return (
        <div className="max-w-4xl mx-auto px-6 lg:px-8 py-8 space-y-6">
            <div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">Pengaturan</div>
                <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Plus Jakarta Sans" }}>
                    Periode Edit & Arsip Bulanan
                </h1>
                <p className="text-sm text-slate-500 mt-1">
                    Atur rentang tanggal saat Admin Prodi boleh menambah/mengubah data, dan kelola snapshot historis.
                </p>
            </div>

            {/* Periode edit */}
            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-6 space-y-4">
                    <div className="flex items-center gap-2">
                        <CalendarClock className="w-5 h-5 text-teal-600" />
                        <h3 className="font-bold text-slate-800">Periode Edit Bulanan (Admin Prodi)</h3>
                    </div>
                    <p className="text-sm text-slate-500">
                        Di luar rentang tanggal ini, Admin Prodi hanya bisa melihat & export data — tombol Tambah, Edit,
                        Hapus, dan Import Excel akan dinonaktifkan. Super Admin tidak terpengaruh.
                    </p>
                    <p className="text-xs text-slate-500">
                        Pilih "Akhir bulan" agar periode berakhir di tanggal terakhir tiap bulan (28/29 Februari, 30, atau 31).
                    </p>
                    <div className="flex flex-wrap items-end gap-4">
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Mulai</Label>
                            <Select value={mulai} onValueChange={setMulai}>
                                <SelectTrigger className="w-28 mt-1"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {DAYS_MULAI.map((d) => <SelectItem key={d} value={String(d)}>{d}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="pb-2 text-slate-400">s/d</div>
                        <div>
                            <Label className="text-xs font-semibold uppercase text-slate-600">Tanggal Selesai</Label>
                            <Select value={selesai} onValueChange={setSelesai}>
                                <SelectTrigger className="w-36 mt-1" data-testid="periode-selesai-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {opsiTanggalSelesai(selesai).map((d) => <SelectItem key={d} value={String(d)}>{labelTanggalSelesai(d)}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={save} disabled={saving} className="bg-[#00B9AD] hover:bg-[#60C0D0]">
                            <Save className="w-4 h-4 mr-2" /> {saving ? "Menyimpan..." : "Simpan"}
                        </Button>
                    </div>
                    {cfg && (
                        <div className={`text-sm rounded-lg border p-3 flex items-center gap-2 ${cfg.is_open ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-800"}`}>
                            {cfg.is_open ? <CheckCircle2 className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                            Hari ini tanggal {cfg.hari_ini} (WIB) —
                            {cfg.is_open
                                ? ` periode edit sedang DIBUKA (tanggal ${teksRentangPeriode(cfg)}).`
                                : ` periode edit sedang DITUTUP untuk Admin Prodi.`}
                        </div>
                    )}
                    {cfg?.updated_by && (
                        <div className="text-xs text-slate-400">
                            Terakhir diubah oleh {cfg.updated_by} · {formatWIB(cfg.updated_at) || "-"}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Snapshot */}
            <Card className="border-slate-200 shadow-sm">
                <CardContent className="p-6 space-y-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
                        <div className="flex items-center gap-2">
                            <Archive className="w-5 h-5 text-teal-600" />
                            <h3 className="font-bold text-slate-800">Snapshot / Arsip Bulanan</h3>
                        </div>
                        <Button onClick={() => createSnapshot(false)} disabled={creating} variant="outline">
                            {creating ? "Memproses..." : "Buat Snapshot Bulan Ini"}
                        </Button>
                    </div>
                    <p className="text-sm text-slate-500">
                        Periode edit bulan ini dipakai merapikan data bulan lalu. Snapshot bulan lalu dibuat otomatis
                        tepat setelah periode edit tutup
                        {Number(cfg?.tanggal_selesai) >= AKHIR_BULAN
                            ? " (bila \"Akhir bulan\": tanggal 1 bulan berikutnya, mis. snapshot Oktober dibuat 1 Desember)."
                            : ` (mis. periode berakhir tanggal ${cfg?.tanggal_selesai ?? 10}: snapshot dibuat tanggal ${(cfg?.tanggal_selesai ?? 10) + 1} bulan yang sama untuk bulan sebelumnya).`}{" "}
                        Tombol di atas untuk membuat manual bila diperlukan. Snapshot hanya menyalin data — tidak mengubah data asli.
                    </p>
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full compact">
                                <thead className="bg-slate-100">
                                    <tr className="text-left text-xs uppercase tracking-wider text-slate-600 font-semibold">
                                        <th>Periode</th><th>Dibuat</th><th>Oleh</th><th className="text-right pr-4">Jumlah Peserta</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {snapshots.length === 0 ? (
                                        <tr><td colSpan={4} className="text-center py-8 text-slate-400">Belum ada snapshot.</td></tr>
                                    ) : snapshots.map((s) => (
                                        <tr key={`${s.tahun}-${s.bulan}`} className="border-t border-slate-200">
                                            <td className="font-semibold text-slate-800">{s.label || `${s.bulan}/${s.tahun}`}</td>
                                            <td className="text-xs text-slate-500">{formatWIB(s.created_at) || "-"}</td>
                                            <td className="text-xs font-mono">{s.created_by}</td>
                                            <td className="text-right pr-4 font-semibold">{s.jumlah_peserta ?? "-"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </CardContent>
            </Card>

            <AlertDialog open={!!confirmOverwrite} onOpenChange={(o) => !o && setConfirmOverwrite(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Timpa snapshot yang sudah ada?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmOverwrite} Snapshot lama untuk periode ini akan diganti dengan data terkini.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Batal</AlertDialogCancel>
                        <AlertDialogAction
                            className="bg-rose-600 hover:bg-rose-700"
                            onClick={() => { setConfirmOverwrite(null); createSnapshot(true); }}
                        >
                            Ya, Timpa
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
